import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../client';
import { queryKeys, PENDING_POLL_MS } from './keys';
import type { Disposition, IdeaCatalogue, IdeaDetail, IdeaFilterParams, IdeaInput, IdeaList, IdeaListParams, IdeaPoints, Research } from '../types';

function filterQuery(filters: IdeaFilterParams, query = new URLSearchParams()): URLSearchParams {
  filters.dispositions.forEach((d) => query.append('disposition', d));
  filters.industries.forEach((i) => query.append('industry', i));
  filters.outcomes.forEach((o) => query.append('verdict', o));
  if (filters.q?.trim()) query.set('q', filters.q.trim());
  return query;
}

function ideaQueryString(params: IdeaListParams): string {
  const query = new URLSearchParams({
    sort: params.sort,
    order: params.order,
    page: String(params.page),
    page_size: String(params.page_size),
  });
  return filterQuery(params, query).toString();
}

const isPending = (status: string | undefined) => status === 'queued' || status === 'researching';

export function useIdeaCatalogue() {
  return useQuery({ queryKey: queryKeys.ideaCatalogue, queryFn: () => api<IdeaCatalogue>('/api/ideas/catalogue') });
}

export function useIdeas(params: IdeaListParams, enabled = true) {
  return useQuery({
    enabled,
    queryKey: queryKeys.ideas(params),
    queryFn: () => api<IdeaList>(`/api/ideas?${ideaQueryString(params)}`),
    placeholderData: (previous) => previous,
    refetchInterval: (query) =>
      query.state.data?.ideas.some((idea) => isPending(idea.research_status)) ? PENDING_POLL_MS : false,
  });
}

/** Every idea matching the filters, for the opportunity matrix. */
export function useIdeaPoints(filters: IdeaFilterParams, enabled = true) {
  const key: IdeaFilterParams = {
    dispositions: filters.dispositions,
    industries: filters.industries,
    outcomes: filters.outcomes,
    q: filters.q ?? '',
  };
  return useQuery({
    enabled,
    queryKey: queryKeys.ideaPoints(key),
    queryFn: () => api<IdeaPoints>(`/api/ideas/points?${filterQuery(key).toString()}`),
    placeholderData: (previous) => previous,
    refetchInterval: (query) =>
      query.state.data?.points.some((p) => isPending(p.research_status)) ? PENDING_POLL_MS : false,
  });
}

export function useIdea(id: string, enabled = true) {
  return useQuery({
    enabled,
    queryKey: queryKeys.idea(id),
    queryFn: () => api<IdeaDetail>(`/api/ideas/${id}`),
    refetchInterval: (query) => (isPending(query.state.data?.research_status) ? PENDING_POLL_MS : false),
  });
}

export function useIdeaRun(id: string, runId: string | null) {
  return useQuery({
    queryKey: queryKeys.ideaRun(id, runId ?? ''),
    queryFn: () => api<Research>(`/api/ideas/${id}/research/${runId}`),
    enabled: runId !== null,
    staleTime: Infinity, // a finished run never changes
  });
}

/** Any change to an idea refreshes the matrix and that idea's page. */
function useIdeaMutation<TVariables>(
  request: (variables: TVariables) => Promise<IdeaDetail>,
  success: (idea: IdeaDetail, variables: TVariables) => string,
) {
  const client = useQueryClient();
  return useMutation({
    meta: { success },
    mutationFn: request,
    onSuccess: (idea) => {
      client.setQueryData(queryKeys.idea(idea.id), idea);
      void client.invalidateQueries({ queryKey: ['ideas', 'list'] });
      void client.invalidateQueries({ queryKey: queryKeys.industries });
      void client.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useSubmitIdea() {
  return useIdeaMutation(
    (body: IdeaInput) => api<IdeaDetail>('/api/ideas', 'POST', body),
    () => 'Idea submitted; research has started',
  );
}

export function useRerunResearch() {
  return useIdeaMutation(
    ({ id, note }: { id: string; note?: string }) =>
      api<IdeaDetail>(`/api/ideas/${id}/research`, 'POST', note ? { note } : undefined),
    (_idea, { note }) => (note ? 'Research queued with your refinement' : 'Research queued'),
  );
}

export function useSetDisposition() {
  return useIdeaMutation(({ id, disposition }: { id: string; disposition: Disposition }) =>
    api<IdeaDetail>(`/api/ideas/${id}/disposition`, 'PATCH', { disposition }),
    (_idea, { disposition }) =>
      ({ active: 'Idea reactivated', parked: 'Idea parked', archived: 'Idea archived' })[disposition],
  );
}

export function useSetIdeaIndustry() {
  return useIdeaMutation(({ id, industry_id }: { id: string; industry_id: string | null }) =>
    api<IdeaDetail>(`/api/ideas/${id}/industry`, 'PATCH', { industry_id }),
    (idea) => (idea.industry ? `Industry set to ${idea.industry.name}` : 'Industry cleared'),
  );
}
